/**
 * Interactive recovery for a console session whose silent renewal is gone
 * (specs/20260916-cortex-console-session-keep-alive design.md section 5.5).
 *
 * This is the only module in the console that may call ``loginRedirect`` outside the
 * sign-in page itself, and only ``getIdToken`` on a user-initiated path may call it.
 * The keep-alive timer has no import path here - see sessionKeepAlive.ts.
 *
 * Classifier copied from the product SPA (code/frontend/src/auth/msalSilentRecovery.ts,
 * D37), not imported.
 */
import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { CONSOLE_LOGIN_SCOPES, getConsoleMsalInstance } from "./msalInstance";

const SILENT_FAIL_RECOVER_WITH_REDIRECT: ReadonlySet<string> = new Set([
  BrowserAuthErrorCodes.monitorWindowTimeout,
  BrowserAuthErrorCodes.monitorPopupTimeout,
  BrowserAuthErrorCodes.iframeClosedPrematurely,
  BrowserAuthErrorCodes.redirectInIframe,
  BrowserAuthErrorCodes.blockIframeReload,
  BrowserAuthErrorCodes.blockNestedPopups,
]);

/**
 * ``true`` when MSAL has said, in its own error types, that only an interactive
 * sign-in can produce a token now. A network error or any other rejection is not
 * that: those are safe failures that keep the current token.
 */
export function needsInteractiveRecovery(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }
  if (
    error instanceof BrowserAuthError &&
    SILENT_FAIL_RECOVER_WITH_REDIRECT.has(error.errorCode)
  ) {
    return true;
  }
  return false;
}

function currentPage(): string {
  if (typeof window !== "undefined" && window.location) {
    return window.location.href || `${window.location.origin}/`;
  }
  return "/";
}

/**
 * Send the operator to Microsoft and back to the page they were on. The console's
 * existing ``handleRedirectPromise`` on init picks the account back up on return.
 */
export async function redirectForFreshConsoleSession(startPage?: string): Promise<void> {
  await getConsoleMsalInstance().loginRedirect({
    scopes: CONSOLE_LOGIN_SCOPES,
    redirectStartPage: startPage ?? currentPage(),
  });
}
